<template>
  <article class="page-sitemap" :lang="zh ? 'zh-Hant' : 'en'">
    <header class="sm-head">
      <p class="sm-kicker">CHE-YU WU / SITEMAP</p>
      <h1>{{ t.title }}</h1>
      <p class="sm-intro">{{ t.intro }}</p>
    </header>

    <!-- 統計條（infra 的成本彙總條）：大數字＋拆分＋一顆說明膠囊＋右邊的操作提示 -->
    <div class="sm-summary">
      <span class="sm-big">{{ fmt(stats.pages) }}<small>{{ t.pagesUnit }}</small></span>
      <span class="sm-muted">{{ t.breakdown(stats) }}</span>
      <span class="sm-chip" :title="t.noindexTip">{{ t.indexed(stats) }}</span>
      <span class="sm-hint sm-hint-mouse">{{ t.hint }}</span>
      <span class="sm-hint sm-hint-touch">{{ t.hintTouch }}</span>
    </div>

    <!-- 類型圖例＝篩選（infra 的專案 legend）＋全覽＋全部展開（infra 的「疊上服務層」） -->
    <div class="sm-legend" role="toolbar" :aria-label="t.legendLabel">
      <button
        v-for="k in kinds" :key="k.kind" type="button" class="sm-kind"
        :aria-pressed="activeKind === k.kind" @click="activeKind = activeKind === k.kind ? null : k.kind"
      >
        <span class="sm-swatch" :style="{ background: KIND_COLOR[k.kind] }" aria-hidden="true"></span>{{ k.label }}<span class="sm-n">{{ k.count }}</span>
      </button>
      <button v-if="activeKind" type="button" class="sm-clear" @click="activeKind = null">{{ t.clearFilter }}</button>
      <span class="sm-legend-gap"></span>
      <button type="button" class="sm-tool" :title="t.fitTip" @click="graph?.fit()">⤢ {{ t.fit }}</button>
      <button type="button" class="sm-tool" :aria-pressed="allOpen" :title="t.expandAllTip" @click="toggleAll">{{ allOpen ? t.collapseAll : t.expandAll }}</button>
    </div>

    <!-- 圖 3/4 ｜ 清單 1/4（infra 的版面；窄螢幕上下排） -->
    <div class="sm-grid">
      <div class="sm-stage">
        <div ref="host" class="sm-graph" data-lenis-prevent>
          <div class="sm-zoom">
            <button type="button" :aria-label="t.zoomIn" @click="graph?.zoomIn()">＋</button>
            <button type="button" :aria-label="t.zoomOut" @click="graph?.zoomOut()">－</button>
          </div>

          <!-- 資訊卡（infra 的 hover 卡／點選卡）：滑鼠滑過或鍵盤聚焦就出現；觸控第一下點選會釘住，附連結 -->
          <div v-if="card" class="sm-card" :class="{ pinned: card.pinned }" role="status">
            <div class="sm-card-top">
              <span class="tag" :class="'k-' + tagKind(card.node)">{{ card.kindLabel }}</span>
              <button v-if="card.pinned" type="button" class="sm-card-close" :aria-label="t.closeCard" @click="graph?.select(null)">×</button>
            </div>
            <div class="sm-card-title">{{ card.title }}</div>
            <div v-if="card.alt" class="sm-card-alt">{{ card.alt }}</div>
            <div v-if="card.urls.length" class="sm-card-urls">
              <a v-for="u in card.urls" :key="u.href" :href="u.href" @click="go($event, u.href)">{{ u.label }} <code>{{ u.href }}</code></a>
            </div>
            <div class="sm-card-meta">{{ card.meta }}</div>
            <ul v-if="card.rels.length" class="sm-card-rels">
              <li v-for="(r, i) in card.rels" :key="i"><span :class="'rel-' + r.type">{{ r.label }}</span> {{ r.dir }} {{ r.other }}</li>
            </ul>
            <p v-if="card.pinned && card.node.type === 'page'" class="sm-card-go">{{ t.tapAgain }}</p>
          </div>
        </div>

        <!-- 關係與節點圖例（infra 常駐左下；這裡改成圖底一條，fit 時節點讓開它；手機排到圖下面） -->
        <div ref="keyEl" class="sm-key" aria-hidden="true">
          <span><svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" class="k-contains" /></svg>{{ t.edge.contains }}</span>
          <span><svg width="22" height="8"><line x1="0" y1="4" x2="16" y2="4" class="k-lists" /><path d="M 16 1 L 22 4 L 16 7 z" class="k-arrow" /></svg>{{ t.edgeListsLong }}</span>
          <span><svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" class="k-also" /></svg>{{ t.edgeAlsoLong }}</span>
          <span><svg width="12" height="12"><circle cx="6" cy="6" r="4.5" class="k-node" /></svg>{{ t.keyBoth }}</span>
          <span><svg width="12" height="12"><circle cx="6" cy="6" r="4.5" class="k-node k-faint" /></svg>{{ t.keyFaint }}</span>
          <span><svg width="12" height="12"><circle cx="6" cy="6" r="4.5" class="k-node k-single" /></svg>{{ t.keySingle }}</span>
          <span><svg width="14" height="14"><circle cx="7" cy="7" r="3.8" class="k-node" /><circle cx="7" cy="7" r="6.2" class="k-open" /></svg>{{ t.keyOpen }}</span>
        </div>
      </div>

      <!-- 全站清單（infra 的資源清單）：每一頁的中英網址都在這裡，預渲染的 HTML 也有，不跑 JS 也讀得到 -->
      <section class="sm-list" aria-labelledby="sm-list-title" data-lenis-prevent>
        <div class="sm-list-head">
          <h2 id="sm-list-title">{{ t.listTitle }}</h2>
          <span class="sm-muted">{{ listStatus }}</span>
        </div>
        <form class="sm-search" role="search" @submit.prevent="revealMatches">
          <label class="visually-hidden" for="sm-search-input">{{ t.searchLabel }}</label>
          <input
            id="sm-search-input" v-model="query" type="search" :placeholder="t.searchPlaceholder"
            autocomplete="off" autocapitalize="off" spellcheck="false" enterkeyhint="search"
          />
        </form>
        <p class="sm-muted sm-search-note" aria-live="polite">{{ searchNote }}</p>
        <details
          v-for="g in listGroups" :key="g.id" class="sm-group" :open="isOpen(g.id)"
          @toggle="onToggle(g.id, $event)"
        >
          <summary>
            <h3 class="sm-group-title">
              <span class="sm-swatch" :style="{ background: KIND_COLOR[g.kind] }" aria-hidden="true"></span>{{ g.label }}<span class="sm-n">×{{ g.count }}</span>
            </h3>
          </summary>
          <template v-if="g.sub">
            <details v-for="s in g.sub" :key="s.id" class="sm-group sm-sub" :open="isOpen(s.id)" @toggle="onToggle(s.id, $event)">
              <summary><h4 class="sm-group-title">{{ s.label }}<span class="sm-n">×{{ s.pages.length }}</span></h4></summary>
              <ul class="sm-rows">
                <li v-for="p in s.pages" :key="p.id" v-bind="rowAttrs(p)">
                  <a :href="urlFor(p, L)" :hreflang="langOf(urlFor(p, L))" @click="go($event, urlFor(p, L))">{{ titleFor(p, L) }}</a>
                  <a v-if="altUrl(p)" class="sm-alt" :href="altUrl(p)" :hreflang="langOf(altUrl(p))" :lang="langOf(altUrl(p))" :aria-label="t.otherLang" @click="go($event, altUrl(p))">{{ t.altBadge }}</a>
                  <span v-else class="sm-only">{{ onlyBadge(p) }}</span>
                </li>
              </ul>
            </details>
          </template>
          <ul v-else class="sm-rows">
            <li v-for="p in g.pages" :key="p.id" v-bind="rowAttrs(p)">
              <a :href="urlFor(p, L)" :hreflang="langOf(urlFor(p, L))" @click="go($event, urlFor(p, L))">{{ titleFor(p, L) }}</a>
              <a v-if="altUrl(p)" class="sm-alt" :href="altUrl(p)" :hreflang="langOf(altUrl(p))" :lang="langOf(altUrl(p))" :aria-label="t.otherLang" @click="go($event, altUrl(p))">{{ t.altBadge }}</a>
              <span v-else class="sm-only">{{ onlyBadge(p) }}</span>
            </li>
          </ul>
        </details>
        <p class="sm-list-foot sm-muted">{{ t.listFoot }}</p>
      </section>
    </div>

    <!-- 底部分析面板（infra 的深度分析面板）：摘要一列＋分頁，固定高度內捲 -->
    <section class="sm-analysis" aria-labelledby="sm-analysis-title">
      <h2 id="sm-analysis-title" class="visually-hidden">{{ t.analysisTitle }}</h2>
      <div class="sm-tabs">
        <button v-for="tb in tabs" :key="tb.id" type="button" class="sm-tab" :aria-pressed="tab === tb.id" @click="tab = tb.id">{{ tb.label }}</button>
      </div>
      <div class="sm-tab-body">
        <ul v-if="tab === 'groups'" class="sm-bars">
          <li v-for="b in bars" :key="b.id">
            <span class="sm-bar-label"><span class="sm-swatch" :style="{ background: KIND_COLOR[b.kind] }" aria-hidden="true"></span>{{ b.label }}</span>
            <span class="sm-bar-track"><span class="sm-bar" :style="{ width: (100 * b.count / barMax).toFixed(2) + '%' }"></span></span>
            <span class="sm-bar-n">{{ fmt(b.count) }}</span>
            <span class="sm-bar-note">{{ b.note }}</span>
          </li>
        </ul>
        <ul v-else-if="tab === 'single'" class="sm-plain">
          <li v-for="p in singles" :key="p.id">
            <a :href="urlFor(p, L)" @click="go($event, urlFor(p, L))">{{ titleFor(p, L) }}</a>
            <span class="sm-muted"> · {{ onlyBadge(p) }}</span>
          </li>
        </ul>
        <ul v-else class="sm-plain">
          <li v-for="p in recent" :key="p.id">
            <span class="sm-date">{{ p.lastmod }}</span>
            <a :href="urlFor(p, L)" @click="go($event, urlFor(p, L))">{{ titleFor(p, L) }}</a>
            <span class="sm-muted"> · {{ kindName(p.kind, L) }}</span>
          </li>
        </ul>
      </div>
    </section>
  </article>
</template>

<script setup>
// 網站地圖頁：Vue 只包一層——資料在 src/runtime/sitemap-model.mjs（純函式），
// 拓樸圖在 src/runtime/sitemap-graph.mjs（移植自 muse-bot memory-viz-v3 的 InfraGraph.tsx，檔頭有出處與改動）。
import { ref, computed, watch, markRaw, onMounted, onBeforeUnmount, shallowRef } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import manifest from '@/data/route-manifest.json'
import {
  buildSitemapModel, visibleGraph, allGroupIds, urlFor, titleFor, searchPages, KIND_COLOR, KIND_ORDER, kindName,
} from '@/runtime/sitemap-model.mjs'
import { createSitemapGraph } from '@/runtime/sitemap-graph.mjs'

const router = useRouter()
const { locale: i18nLocale } = useI18n()
const zh = computed(() => i18nLocale.value === 'zh')
const L = computed(() => (zh.value ? 'zh' : 'en'))
const model = markRaw(buildSitemapModel(manifest))
const stats = model.stats
const nf = new Intl.NumberFormat('en-US')
const fmt = n => nf.format(n)

const T = {
  zh: {
    title: '網站地圖',
    intro: 'cheyuwu.com 的每一個公開頁面，依網址階層與內容類型攤成一張拓樸圖。點群組展開，點節點就到那一頁。旁邊是同一份完整清單。',
    pagesUnit: ' 頁',
    breakdown: s => `${fmt(s.routes)} 個網址（英文 ${fmt(s.en)}・中文 ${fmt(s.zh)}）・${s.groups} 群・最近更新 ${s.latest}`,
    indexed: s => `收進搜尋引擎 ${fmt(s.routes - s.noindexRoutes)}・不收 ${fmt(s.noindexRoutes)}`,
    noindexTip: '說明太少的程式草圖頁標了 noindex：頁面照樣公開、照樣連得到，只是不請搜尋引擎收錄（2026-09-14 起）。',
    hint: '滾輪縮放・空白處拖曳平移・節點可拖・雙擊全覽',
    hintTouch: '雙指縮放・拖曳平移・點一下節點看資訊，再點一下前往',
    legendLabel: '依類型篩選',
    clearFilter: '清除篩選',
    fit: '全覽', fitTip: '把整張圖放進框裡（雙擊圖亦可）',
    expandAll: '全部展開', collapseAll: '全部收合',
    expandAllTip: '把每一群都展開成一頁一個節點（程式草圖先分年份）',
    zoomIn: '放大', zoomOut: '縮小', closeCard: '關閉資訊卡',
    graph: '網站拓樸圖：方向鍵平移，加減號縮放，0 全覽。Tab 逐一走過節點，Enter 開啟或展開',
    edge: { contains: '包含', lists: '收錄', also: '也收錄' },
    edgeListsLong: '收錄（箭頭指向被收錄的群）',
    edgeAlsoLong: '另一個索引頁也收錄',
    keyBoth: '中英都有', keyFaint: '不進搜尋索引', keySingle: '只有一種語言', keyOpen: '已展開的群',
    listTitle: '全站清單',
    listFoot: '每一列連到這一頁，右邊的 EN 是同一頁的英文版。清單、拓樸圖與 sitemap.xml 出自同一份路由清單。',
    searchLabel: '在網站地圖裡找頁面', searchPlaceholder: '找頁面：標題或網址，例如 靈魂、2020、/td/',
    altBadge: 'EN', otherLang: '英文版', onlyZh: '只有中文', onlyEn: '只有英文',
    loose: '頁面',
    matches: n => `${n} 符合`,
    found: (n, g) => `符合 ${n} 頁，在 ${g} 群裡。按 Enter 在圖上展開它們`,
    foundMany: n => `符合 ${n} 頁（太多了，先縮小範圍再展開）`,
    none: '沒有符合的頁面',
    listStatus: (n, m) => (n === m ? `${fmt(n)} 頁` : `${fmt(n)}／${fmt(m)} 頁`),
    analysisTitle: '分析',
    tabGroups: '各群頁數', tabSingle: n => `只有一種語言 ${n}`, tabRecent: '最近更新',
    barNote: (noi, single) => [noi ? `${noi} 不進索引` : '', single ? `${single} 只有中文` : ''].filter(Boolean).join('・'),
    groupState: (open, n) => (open ? `已展開，${n} 頁` : `收合中，點一下展開這 ${n} 頁`),
    pageMeta: p => [p.lastmod ? `更新 ${p.lastmod}` : '', p.urls.en && p.urls.zh ? '中英都有' : p.urls.zh ? '只有中文' : '只有英文', p.noindex ? '不進搜尋引擎索引' : ''].filter(Boolean).join('・'),
    tapAgain: '再點一次節點就前往這一頁',
    hasMembers: n => `成員 ${n} 頁`,
    urlZh: '中文', urlEn: 'EN',
    rootKind: '首頁', groupKind: '群組',
  },
  en: {
    title: 'Sitemap',
    intro: 'Every public page on cheyuwu.com, laid out as one map by address and type. Open a group to see its pages, select a page to go there; the full list sits alongside.',
    pagesUnit: ' pages',
    breakdown: s => `${fmt(s.routes)} URLs (English ${fmt(s.en)} · Chinese ${fmt(s.zh)}) · ${s.groups} groups · last updated ${s.latest}`,
    indexed: s => `In search engines ${fmt(s.routes - s.noindexRoutes)} · left out ${fmt(s.noindexRoutes)}`,
    noindexTip: 'Code-sketch pages with too little text carry noindex: they stay public and linked, they just do not ask search engines to index them (since 14 September 2026).',
    hint: 'Scroll to zoom · drag empty space to pan · drag nodes · double-click to fit',
    hintTouch: 'Pinch to zoom · drag to pan · tap a node for details, tap again to go',
    legendLabel: 'Filter by type',
    clearFilter: 'Clear filter',
    fit: 'Fit', fitTip: 'Fit the whole map in the frame (or double-click it)',
    expandAll: 'Expand all', collapseAll: 'Collapse all',
    expandAllTip: 'Open every group into one node per page (code sketches go by year first)',
    zoomIn: 'Zoom in', zoomOut: 'Zoom out', closeCard: 'Close the card',
    graph: 'Site map: arrow keys pan, plus and minus zoom, 0 fits; Tab walks the nodes, Enter opens or expands',
    edge: { contains: 'contains', lists: 'lists', also: 'also lists' },
    edgeListsLong: 'Lists (arrow points at the listed group)',
    edgeAlsoLong: 'Also listed by another index',
    keyBoth: 'English and Chinese', keyFaint: 'not indexed', keySingle: 'one language only', keyOpen: 'expanded group',
    listTitle: 'Every page',
    listFoot: 'Each row links to its page; 中 on the right is the Chinese version. The list, the map and sitemap.xml come from one route list.',
    searchLabel: 'Find a page in the sitemap', searchPlaceholder: 'Find a page by title or address, e.g. soul, 2020, /td/',
    altBadge: '中', otherLang: 'Chinese version', onlyZh: 'Chinese only', onlyEn: 'English only',
    loose: 'Pages',
    matches: n => `${n} match${n === 1 ? '' : 'es'}`,
    found: (n, g) => `${n} matching page${n === 1 ? '' : 's'} in ${g} group${g === 1 ? '' : 's'}; press Enter to open them on the map`,
    foundMany: n => `${n} matching pages (narrow it down before opening them on the map)`,
    none: 'No matching pages',
    listStatus: (n, m) => (n === m ? `${fmt(n)} pages` : `${fmt(n)} of ${fmt(m)} pages`),
    analysisTitle: 'Analysis',
    tabGroups: 'Pages per group', tabSingle: n => `One language only ${n}`, tabRecent: 'Recently updated',
    barNote: (noi, single) => [noi ? `${noi} not indexed` : '', single ? `${single} Chinese only` : ''].filter(Boolean).join(' · '),
    groupState: (open, n) => (open ? `Expanded, ${n} pages` : `Collapsed; select to open its ${n} pages`),
    pageMeta: p => [p.lastmod ? `Updated ${p.lastmod}` : '', p.urls.en && p.urls.zh ? 'English and Chinese' : p.urls.zh ? 'Chinese only' : 'English only', p.noindex ? 'not indexed by search engines' : ''].filter(Boolean).join(' · '),
    tapAgain: 'Tap the node again to go to this page',
    hasMembers: n => `${n} member pages`,
    urlZh: '中文', urlEn: 'EN',
    rootKind: 'Home', groupKind: 'Group',
  },
}
const t = computed(() => T[L.value])

// ── 狀態：展開的群組（圖與清單共用）、類型篩選、搜尋 ──
const expanded = ref(new Set())
const openLoose = ref(true)
const activeKind = ref(null)
const query = ref('')
const hits = computed(() => searchPages(model, query.value))
const searching = computed(() => !!hits.value)
const allIds = allGroupIds(model)
const allOpen = computed(() => allIds.every(id => expanded.value.has(id)))

const kinds = computed(() => {
  const count = {}
  for (const p of model.pages) count[p.kind] = (count[p.kind] || 0) + 1
  return KIND_ORDER.filter(k => count[k]).map(k => ({ kind: k, label: kindName(k, L.value), count: count[k] }))
})

// ── 清單 ──
const keep = p => (!activeKind.value || p.kind === activeKind.value) && (!hits.value || hits.value.has(p.id))
const byId = id => model.byId.get(id)
const listGroups = computed(() => {
  const out = []
  const loose = model.pages.filter(p => !p.group && keep(p))
  if (loose.length) out.push({ id: 'loose', label: t.value.loose, kind: 'page', pages: loose, count: loose.length })
  for (const g of model.groups) {
    if (g.subgroups) {
      const sub = g.subgroups.map(s => ({ id: s.id, label: s.year, pages: s.members.map(byId).filter(keep) })).filter(s => s.pages.length)
      const count = sub.reduce((n, s) => n + s.pages.length, 0)
      if (count) out.push({ id: g.id, label: titleFor(g, L.value), kind: g.kind, sub, count })
    } else {
      const pages = g.members.map(byId).filter(keep)
      if (pages.length) out.push({ id: g.id, label: titleFor(g, L.value), kind: g.kind, pages, count: pages.length })
    }
  }
  return out
})
const shownCount = computed(() => listGroups.value.reduce((n, g) => n + g.count, 0))
const listStatus = computed(() => t.value.listStatus(shownCount.value, stats.pages))
const isOpen = id => (searching.value ? true : id === 'loose' ? openLoose.value : expanded.value.has(id))
function onToggle(id, event) {
  if (searching.value) return
  const open = event.target.open
  if (id === 'loose') { openLoose.value = open; return }
  if (open !== expanded.value.has(id)) setExpanded(id, open)
}
function setExpanded(id, open) {
  const next = new Set(expanded.value)
  if (open) next.add(id); else next.delete(id)
  // 收起一群時，它的年份子群也一起收
  if (!open) for (const sid of allIds) if (sid.startsWith(id + ':')) next.delete(sid)
  expanded.value = next
}
function toggleAll() { expanded.value = allOpen.value ? new Set() : new Set(allIds) }
const searchNote = computed(() => {
  if (!hits.value) return ''
  const n = hits.value.size
  if (!n) return t.value.none
  const groups = new Set([...hits.value].map(id => byId(id).group || 'loose'))
  return n > 150 ? t.value.foundMany(n) : t.value.found(n, groups.size)
})
// Enter：把符合的頁所在的群展開，鏡頭框住它們
function revealMatches() {
  const h = hits.value
  if (!h || !h.size || h.size > 150) return
  const next = new Set(expanded.value)
  for (const id of h) {
    const p = byId(id)
    if (p.group) next.add(p.group)
    if (p.subgroup) next.add(p.subgroup)
  }
  pendingFit = h
  expanded.value = next   // 新的 Set ⇒ watch 一定會觸發 syncGraph（就算本來就全展開，也會重新框住符合的頁）
}

const altUrl = p => (p.urls.en && p.urls.zh ? p.urls[L.value === 'zh' ? 'en' : 'zh'] : null)
const onlyBadge = p => (p.urls.zh ? t.value.onlyZh : t.value.onlyEn)
const langOf = url => (url.startsWith('/zh/') ? 'zh-Hant' : 'en')
const visibleAnchor = id => {
  const p = byId(id)
  if (!p || p.type !== 'page' || !p.group) return id
  if (p.subgroup && !expanded.value.has(p.subgroup)) return expanded.value.has(p.group) ? p.subgroup : p.group
  return expanded.value.has(p.group) ? id : p.group
}
const rowAttrs = p => ({
  class: { 'is-noindex': p.noindex, 'is-sub': !p.group && p.layer >= 2 },
  onPointerenter: () => graph.value?.focus(visibleAnchor(p.id)),
  onPointerleave: () => graph.value?.focus(null),
  onFocusin: () => graph.value?.focus(visibleAnchor(p.id)),
  onFocusout: () => graph.value?.focus(null),
})
function go(event, url) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  event.preventDefault()
  router.push(url)
}

// ── 分析面板 ──
const tab = ref('groups')
const singles = model.pages.filter(p => !(p.urls.en && p.urls.zh))
const recent = [...model.pages].filter(p => p.lastmod).sort((a, b) => b.lastmod.localeCompare(a.lastmod) || a.key.localeCompare(b.key)).slice(0, 15)
const tabs = computed(() => [
  { id: 'groups', label: t.value.tabGroups },
  { id: 'single', label: t.value.tabSingle(singles.length) },
  { id: 'recent', label: t.value.tabRecent },
])
const bars = computed(() => {
  const rows = [{ id: 'loose', label: t.value.loose, kind: 'page', pages: model.pages.filter(p => !p.group) }]
    .concat(model.groups.map(g => ({ id: g.id, label: titleFor(g, L.value), kind: g.kind, pages: g.members.map(byId) })))
  return rows.map(r => ({ ...r, count: r.pages.length, note: t.value.barNote(r.pages.filter(p => p.noindex).length, r.pages.filter(p => !p.urls.en).length) }))
})
const barMax = computed(() => Math.max(...bars.value.map(b => b.count)))

// ── 資訊卡 ──
const inspectId = ref(null)
const selectedId = ref(null)
const tagKind = n => (n.type === 'page' && n.key === '/' ? 'page' : n.kind)
const card = computed(() => {
  const id = inspectId.value || selectedId.value
  const n = id && byId(id)
  if (!n) return null
  const lang = L.value, other = lang === 'zh' ? 'en' : 'zh'
  const pinned = id === selectedId.value
  const rels = []
  for (const e of model.edges) {
    if (e.source !== id && e.target !== id) continue
    if (e.member && e.source === id) continue   // 群→成員的線上百條，卡片上只寫「×N」
    const o = byId(e.source === id ? e.target : e.source)
    rels.push({ type: e.type, label: t.value.edge[e.type], dir: e.source === id ? '→' : '←', other: o.type === 'group' ? `${titleFor(o, lang)} ×${o.count}` : titleFor(o, lang) })
  }
  if (n.type === 'group') {
    return {
      node: n, pinned, kindLabel: t.value.groupKind + (lang === 'zh' ? '・' : ' · ') + kindName(n.kind, lang), title: `${titleFor(n, lang)} ×${n.count}`, alt: '',
      urls: [], meta: t.value.groupState(expanded.value.has(n.id), n.count), rels: rels.slice(0, 8),
    }
  }
  const urls = []
  if (n.urls[lang]) urls.push({ label: lang === 'zh' ? t.value.urlZh : t.value.urlEn, href: n.urls[lang] })
  if (n.urls[other]) urls.push({ label: other === 'zh' ? t.value.urlZh : t.value.urlEn, href: n.urls[other] })
  const alt = n.title[other] && n.title[other] !== n.title[lang] && n.key !== '/' ? n.title[other] : ''
  return {
    node: n, pinned, kindLabel: n.key === '/' ? t.value.rootKind : kindName(n.kind, lang), title: titleFor(n, lang), alt, urls,
    meta: t.value.pageMeta(n), rels: rels.slice(0, 8),
  }
})

// ── 拓樸圖 ──
const host = ref(null)
const keyEl = ref(null)
const graph = shallowRef(null)
// 圖例條疊在圖底（桌機）時，fit 讓開它的高度；手機圖例排在圖外，不用讓
const insets = () => {
  const key = keyEl.value
  const overlay = key && getComputedStyle(key).position === 'absolute'
  return { top: 8, bottom: overlay ? key.offsetHeight + 14 : 8, left: 48, right: 8 }
}
let pendingFit = null
function syncGraph() {
  if (!graph.value) return
  graph.value.setGraph(visibleGraph(model, expanded.value), { expandedIds: expanded.value, fitIds: pendingFit })
  pendingFit = null
}
watch(expanded, syncGraph)
watch(activeKind, k => graph.value?.setFilter(k))
watch(hits, h => graph.value?.setMatches(h))
function activate(n, { newTab } = {}) {
  if (n.type === 'group') { setExpanded(n.id, !expanded.value.has(n.id)); return }
  const url = urlFor(n, L.value)
  if (newTab) window.open(url, '_blank', 'noopener')
  else router.push(url)
}
onMounted(() => {
  const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
  graph.value = markRaw(createSitemapGraph(host.value, {
    locale: L.value,
    reducedMotion,
    insets,
    labels: {
      graph: t.value.graph, edge: t.value.edge, matches: t.value.matches,
      pagesUnit: zh.value ? ' 頁' : ' pages',
      expandedSuffix: zh.value ? '，已展開' : ', expanded', collapsedSuffix: zh.value ? '，收合中' : ', collapsed',
    },
    onActivate: activate,
    onHover: id => { inspectId.value = id },
    onSelect: id => { selectedId.value = id; if (!id) inspectId.value = null },
  }))
  syncGraph()
})
onBeforeUnmount(() => { graph.value?.destroy(); graph.value = null })
</script>

<style scoped>
.page-sitemap { max-width: var(--content-w); margin: 0 auto; padding: 28px 16px 0; text-align: left; }
.visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.sm-head { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: end; column-gap: 28px; margin-bottom: 14px; }
.sm-kicker { grid-column: 1 / -1; margin: 0 0 6px; font: 500 0.78rem var(--font-body); letter-spacing: 0.18em; color: var(--ink-3); }
h1 { font-family: var(--font-display); font-size: clamp(2rem, 3.4vw, 2.8rem); font-weight: 600; margin: 0; line-height: 1.1; }
.sm-intro { margin: 0; max-width: 52em; color: var(--ink-2); line-height: 1.65; font-size: 0.95rem; }
.sm-muted { color: var(--ink-3); font-size: 0.82rem; }

.sm-summary { display: flex; flex-wrap: wrap; gap: 6px 16px; align-items: baseline; padding: 4px 2px 10px; }
.sm-big { font: 600 1.6rem var(--font-body); font-variant-numeric: tabular-nums; color: var(--ink); }
.sm-big small { font-size: 0.8rem; font-weight: 400; color: var(--ink-3); }
.sm-summary .sm-muted { font-variant-numeric: tabular-nums; }
.sm-chip { font-size: 0.78rem; color: var(--ink-2); background: var(--tint-2); border-radius: var(--r-pill); padding: 2px 11px; cursor: help; font-variant-numeric: tabular-nums; }
.sm-hint { margin-left: auto; font-size: 0.75rem; color: var(--ink-3); }

.sm-legend { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding-bottom: 10px; }
.sm-legend-gap { flex: 1 1 0; }
.sm-kind, .sm-tool, .sm-clear, .sm-tab {
  display: inline-flex; align-items: center; gap: 6px; min-height: 30px; padding: 2px 11px; border: 0; border-radius: var(--r-pill);
  background: var(--tint-2); color: var(--ink-2); font: 400 0.78rem var(--font-body); cursor: pointer; transition: var(--t-hover); white-space: nowrap;
}
.sm-kind:hover, .sm-tool:hover, .sm-tab:hover { background: var(--tint-3); color: var(--ink); }
.sm-kind[aria-pressed="true"], .sm-tool[aria-pressed="true"], .sm-tab[aria-pressed="true"] { background: var(--ink); color: #000; }
.sm-clear { background: none; text-decoration: underline; text-underline-offset: 3px; color: var(--ink-3); }
.sm-swatch { flex: none; display: inline-block; width: 8px; height: 8px; border-radius: 50%; }
.sm-n { margin-left: 2px; color: var(--ink-3); font-variant-numeric: tabular-nums; font-size: 0.92em; }
.sm-kind[aria-pressed="true"] .sm-n { color: #333; }

.sm-grid { display: grid; grid-template-columns: minmax(0, 3fr) minmax(260px, 1fr); gap: 12px; align-items: stretch; }
.sm-stage { position: relative; min-width: 0; }
/* overflow: clip 而不是 hidden：hidden 的框仍可被程式捲動，Tab 聚焦到框外的節點時瀏覽器會把整張 svg 捲歪 */
.sm-graph { position: relative; height: min(62vh, 660px); min-height: 380px; border-radius: var(--r-md); overflow: hidden; overflow: clip; background: var(--tint-1); touch-action: none; }
.sm-zoom { position: absolute; top: 8px; left: 8px; display: flex; flex-direction: column; gap: 4px; z-index: 2; }
.sm-zoom button { width: 34px; height: 34px; border: 0; border-radius: var(--r-sm); background: var(--bg2); color: var(--ink-2); font: 400 1rem var(--font-body); cursor: pointer; transition: var(--t-hover); }
.sm-zoom button:hover { color: var(--ink); background: var(--tint-3); }

.sm-card { position: absolute; top: 8px; right: 8px; width: 290px; max-width: min(290px, calc(100% - 60px)); z-index: 3; background: var(--bg1); border-radius: var(--r-md); padding: 12px 14px; font-size: 0.8rem; box-shadow: 0 6px 24px rgba(0,0,0,.45); pointer-events: none; }
.sm-card.pinned { pointer-events: auto; }
.sm-card-top { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.sm-card-close { border: 0; background: none; color: var(--ink-3); font-size: 1.2rem; line-height: 1; cursor: pointer; min-width: 32px; min-height: 32px; }
.sm-card-title { margin-top: 6px; font: 600 0.95rem/1.4 var(--font-body); color: var(--ink); }
.sm-card-alt { margin-top: 2px; color: var(--ink-3); line-height: 1.4; }
.sm-card-urls { margin-top: 8px; display: grid; gap: 3px; }
.sm-card-urls a { color: var(--ink-2); text-decoration: none; overflow-wrap: anywhere; }
.sm-card-urls a:hover { color: var(--gold); }
.sm-card-urls code { font-size: 0.74rem; color: var(--ink-3); background: none; padding: 0; }
.sm-card-meta { margin-top: 6px; color: var(--ink-3); line-height: 1.5; }
.sm-card-rels { list-style: none; margin: 8px 0 0; padding: 0; display: grid; gap: 1px; color: var(--ink-3); line-height: 1.55; }
.sm-card-rels .rel-contains, .sm-card-rels .rel-also { color: var(--ink-2); }
.sm-card-rels .rel-lists { color: var(--ink); font-weight: 500; }
.sm-card-go { margin: 8px 0 0; color: var(--gold); }

.sm-key { position: absolute; left: 8px; right: 8px; bottom: 8px; z-index: 2; pointer-events: none; display: flex; flex-wrap: wrap; gap: 2px 16px; align-items: center; font-size: 0.72rem; line-height: 1.7; color: var(--ink-2); background: rgba(13,13,15,.82); border-radius: var(--r-sm); padding: 4px 12px; }
.sm-key span { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.sm-key line { stroke-width: 1.6; }
.sm-key .k-contains { stroke: var(--ink-3); }
.sm-key .k-lists { stroke: var(--ink-2); }
.sm-key .k-arrow { fill: var(--ink-2); }
.sm-key .k-also { stroke: var(--ink-3); stroke-dasharray: 2 4; }
.sm-key .k-node { fill: var(--k-work); stroke: rgba(0,0,0,.35); }
.sm-key .k-faint { fill-opacity: .4; }
.sm-key .k-single { stroke: var(--ink); stroke-dasharray: 2 2; }
.sm-key .k-open { fill: none; stroke: var(--ink-2); stroke-width: 1; }
.sm-hint-touch { display: none; }
@media (hover: none) { .sm-hint-mouse { display: none; } .sm-hint-touch { display: inline; } }

.sm-list { height: min(62vh, 660px); min-height: 380px; overflow-y: auto; overscroll-behavior: contain; border-radius: var(--r-md); background: var(--tint-1); padding: 10px 12px; }
.sm-list-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 8px; }
.sm-list-head h2 { margin: 0; font: 600 0.85rem var(--font-body); color: var(--ink); letter-spacing: 0.02em; }
.sm-search input { width: 100%; background: var(--bg2); border: 0; border-radius: var(--r-pill); color: var(--ink); padding: 8px 14px; font: 400 0.85rem var(--font-body); outline: none; -webkit-appearance: none; appearance: none; }
.sm-search input:focus-visible { box-shadow: 0 0 0 1px var(--gold); }
.sm-search input::placeholder { color: var(--ink-3); }
.sm-search-note { min-height: 1.2em; margin: 6px 4px 6px; font-size: 0.74rem; }
.sm-group { margin-bottom: 2px; }
.sm-group > summary { list-style: none; cursor: pointer; border-radius: var(--r-sm); padding: 4px 6px; transition: var(--t-hover); }
.sm-group > summary::-webkit-details-marker { display: none; }
.sm-group > summary:hover { background: var(--tint-2); }
.sm-group[open] > summary { background: var(--tint-1); }
.sm-group-title { display: flex; align-items: center; gap: 7px; margin: 0; font: 500 0.82rem var(--font-body); color: var(--ink); }
.sm-group-title::before { content: '▸'; width: 9px; font-size: 0.66rem; color: var(--ink-3); }
.sm-group[open] > summary .sm-group-title::before { content: '▾'; }
.sm-sub { margin-left: 14px; }
.sm-sub .sm-group-title { font-size: 0.78rem; color: var(--ink-2); }
.sm-rows { list-style: none; margin: 2px 0 6px; padding: 0; }
.sm-rows li { display: flex; align-items: baseline; gap: 6px; padding: 2px 6px 2px 22px; border-radius: 5px; font-size: 0.78rem; line-height: 1.45; }
.sm-rows li:hover, .sm-rows li:focus-within { background: var(--tint-2); }
.sm-rows li.is-sub { padding-left: 34px; }
.sm-rows li.is-noindex > a:first-child { color: var(--ink-3); }
.sm-rows a { color: var(--ink-2); text-decoration: none; }
.sm-rows a:first-child { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.sm-rows a:hover { color: var(--gold); }
.sm-alt, .sm-only { flex: none; font-size: 0.68rem; letter-spacing: 0.04em; color: var(--ink-3); }
.sm-alt { padding: 0 6px; border-radius: var(--r-pill); background: var(--tint-2); }
.sm-list-foot { margin: 10px 2px 2px; font-size: 0.7rem; line-height: 1.6; padding-top: 8px; border-top: 1px solid var(--line); }

.sm-analysis { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--line); }
.sm-tabs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.sm-tab-body { max-height: 270px; overflow-y: auto; overscroll-behavior: contain; font-size: 0.8rem; line-height: 1.5; padding-right: 4px; }
.sm-bars { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px; }
.sm-bars li { display: grid; grid-template-columns: minmax(150px, 220px) minmax(80px, 1fr) 52px minmax(0, 220px); gap: 10px; align-items: center; }
.sm-bar-label { display: inline-flex; align-items: center; gap: 7px; color: var(--ink-2); }
.sm-bar-track { height: 6px; border-radius: 3px; background: var(--tint-1); overflow: hidden; }
.sm-bar { display: block; height: 100%; background: var(--ink-3); border-radius: 3px; }
.sm-bar-n { text-align: right; font-variant-numeric: tabular-nums; color: var(--ink); }
.sm-bar-note { color: var(--ink-3); font-size: 0.74rem; }
.sm-plain { list-style: none; margin: 0; padding: 0; columns: 2 340px; column-gap: 28px; }
.sm-plain li { break-inside: avoid; padding: 2px 0; }
.sm-plain a { color: var(--ink-2); text-decoration: none; }
.sm-plain a:hover { color: var(--gold); }
.sm-date { display: inline-block; min-width: 6.2em; color: var(--ink-3); font-variant-numeric: tabular-nums; font-size: 0.76rem; }

@media (max-width: 899px) {
  .sm-grid { grid-template-columns: 1fr; }
  .sm-list { height: auto; min-height: 0; overflow: visible; }
  .sm-hint { margin-left: 0; flex-basis: 100%; }
}
@media (max-width: 640px) {
  .page-sitemap { padding-top: 20px; }
  .sm-head { grid-template-columns: 1fr; row-gap: 8px; }
  .sm-graph { height: 62vh; min-height: 360px; max-height: 460px; }
  .sm-key { position: static; margin-top: 8px; background: none; padding: 0 2px; gap: 2px 12px; }
  .sm-legend { flex-wrap: nowrap; overflow-x: auto; margin: 0 -16px; padding: 0 16px 10px; scrollbar-width: none; }
  .sm-legend::-webkit-scrollbar { display: none; }
  .sm-legend-gap { display: none; }
  .sm-card { top: auto; bottom: 8px; left: 8px; right: 8px; width: auto; max-width: none; }
  .sm-zoom button { width: 44px; height: 44px; }
  .sm-kind, .sm-tool, .sm-tab { min-height: 40px; }
  .sm-bars li { grid-template-columns: minmax(0, 1fr) 48px; }
  .sm-bar-track { grid-column: 1 / -1; grid-row: 2; }
  .sm-bar-note { grid-column: 1 / -1; }
  .sm-rows li { padding-top: 6px; padding-bottom: 6px; }
}
</style>

<!-- 圖是 sitemap-graph.mjs 用 DOM API 畫的，scoped 樣式碰不到，這段不加 scoped（全用 .smg- 前綴） -->
<style>
.smg-svg { display: block; cursor: grab; touch-action: none; outline: none; font-family: var(--font-body); }
.smg-svg.is-panning { cursor: grabbing; }
.smg-svg:focus-visible { box-shadow: inset 0 0 0 2px var(--gold); }
.smg-view { opacity: 0; transition: opacity .28s ease; }
.smg-view.is-ready { opacity: 1; }
.smg-edge { stroke: var(--ink-3); stroke-width: 1.5; opacity: .68; transition: opacity var(--dur-quick) var(--ease-out); }
.smg-edge.t-contains { stroke-width: 1.2; opacity: .35; }
.smg-edge.t-contains.is-member { stroke-width: .8; opacity: .22; }
.smg-edge.t-lists { stroke: var(--ink-2); }
.smg-edge.t-also { stroke-dasharray: 2 4; opacity: .3; }
.smg-edge.is-nb { stroke: var(--gold); stroke-width: 2.2; opacity: .95; }
.smg-edge.t-contains.is-member.is-nb { stroke-width: 1.2; }
.smg-edge.is-faded { opacity: .07; }
.smg-edge.is-dim { opacity: .04; }
.smg-arrow { fill: var(--ink-2); opacity: .85; }
.smg-arrow-hot { fill: var(--gold); }
.smg-edge-label { font-size: 10px; font-weight: 600; fill: var(--gold); pointer-events: none; paint-order: stroke; stroke: rgba(0,0,0,.75); stroke-width: 3px; }
.smg-node { cursor: pointer; outline: none; transition: opacity var(--dur-quick) var(--ease-out); }
.smg-node .smg-dot { stroke: rgba(0,0,0,.35); stroke-width: 1; }
.smg-node.t-page:not(.is-leaf):not(.is-hub) .smg-dot { stroke-width: 1; }
.smg-node .smg-icon { fill: none; stroke: rgba(0,0,0,.78); stroke-linecap: round; stroke-linejoin: round; pointer-events: none; }
.smg-node .smg-logo { pointer-events: none; }
.smg-node[data-id="/"] .smg-dot { fill: var(--bg2); stroke: var(--line-2); }
.smg-node.is-noindex .smg-dot { fill-opacity: .4; }
.smg-node.is-single .smg-dot { stroke: var(--ink-2); stroke-dasharray: 2 2; }
.smg-node .smg-open-ring { fill: none; stroke: var(--ink-2); stroke-width: 1; opacity: .7; }
.smg-node .smg-ring { fill: none; stroke: var(--gold); stroke-width: 2; }
.smg-node.is-focus .smg-dot, .smg-node.is-selected .smg-dot, .smg-node:focus-visible .smg-dot { stroke: var(--gold); stroke-width: 2.5; stroke-dasharray: none; }
.smg-node.is-faded, .smg-node.is-dim { opacity: .14; }
.smg-label { font-size: 10.5px; fill: var(--ink); opacity: .95; paint-order: stroke; stroke: rgba(0,0,0,.7); stroke-width: 3px; pointer-events: none; }
.smg-label.is-minor { font-size: 8.5px; opacity: .68; }
.smg-node.is-leaf .smg-label { font-size: 8px; stroke-width: 2.5px; fill: var(--ink-2); opacity: .8; display: none; }
.smg-svg.k-big .smg-node.is-leaf .smg-label, .smg-node.is-leaf.is-focus .smg-label, .smg-node.is-leaf.is-nb .smg-label, .smg-node.is-leaf.is-match .smg-label, .smg-node.is-leaf:focus-visible .smg-label { display: inline; }
.smg-node.is-focus .smg-label { opacity: 1; }
@media (prefers-reduced-motion: reduce) {
  .smg-view, .smg-edge, .smg-node { transition: none; }
}
</style>
